import { lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { Flex } from "@chakra-ui/react";
import { AppToast, DotLoader, EukaBarkProvider } from "@euka-mfes/bark";

import ErrorPageLayout from "@/layouts/ErrorPageLayout";
import ClearStoragePage from "@/pages/ClearStoragePage";
import { getBaseRoute } from "@/utils/route";
import LearningLayout from "@layouts/LearningLayout";
import LessonsLayout from "@layouts/LessonsLayout";
import AuthPageLayout from "@layouts/AuthPageLayout";
import NoAccessPage from "@pages/NoAccessPage";
import StartupRedirectPage from "@pages/StartupRedirectPage";
import LessonRouteErrorBoundary from "@components/LessonRouteErrorBoundary";
import { StudentDataProvider, useStudentData } from "@/context";
import { useServerError } from "@/hooks/useServerError";
import ServerErrorContainer from "@containers/errors/ServerErrorContainer";

/**
 * Inner layout that uses context for theming.
 *
 * When a 5xx server error has been flagged by httpClient, this renders
 * the error page in-place (without changing the URL) so the student can
 * refresh to retry.
 */
const ThemedLayout = () => {
  const { gradeTheme } = useStudentData();
  const { hasError: hasServerError } = useServerError();

  return (
    <EukaBarkProvider gradeTheme={gradeTheme}>
      {hasServerError ? <ServerErrorContainer /> : <Outlet />}
      <AppToast />
    </EukaBarkProvider>
  );
};

/** Root wrapper that provides StudentDataContext to all routes */
const RootLayout = () => (
  <StudentDataProvider>
    <ThemedLayout />
  </StudentDataProvider>
);

/** Centered loading spinner shown while a lazy chunk downloads. */
const ChunkFallback = () => (
  <Flex w="full" minH="50vh" align="center" justify="center">
    <DotLoader isLoaded={false} size="lg" />
  </Flex>
);

/** Wrap a lazy component with Suspense + loading fallback. */
const Lazy = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<ChunkFallback />}>{children}</Suspense>
);

/**
 * Wrapper around `React.lazy()` that retries the dynamic import up to 3 times
 * with exponential backoff (1s, 2s, 4s) before giving up.
 *
 * Handles transient network dropouts that would otherwise cause a chunk load
 * failure and a white screen / React error boundary crash.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3
): React.LazyExoticComponent<T> {
  return lazy(() => {
    const attempt = (remaining: number): Promise<{ default: T }> =>
      importFn().catch((error: unknown) => {
        if (remaining <= 0) throw error;
        const delay = Math.pow(2, retries - remaining) * 1000; // 1s, 2s, 4s
        return new Promise<{ default: T }>(resolve =>
          setTimeout(() => resolve(attempt(remaining - 1)), delay)
        );
      });
    return attempt(retries);
  });
}

// --- Lazy-loaded pages (with retry) ---
// Lesson pages
const IntroductionPage = lazyWithRetry(() => import("@pages/lessons/IntroductionPage"));
const LearnPage = lazyWithRetry(() => import("@pages/lessons/LearnPage"));
const PracticePage = lazyWithRetry(() => import("@pages/lessons/PracticePage"));
const ApplyPage = lazyWithRetry(() => import("@pages/lessons/ApplyPage"));
const ReflectPage = lazyWithRetry(() => import("@pages/lessons/ReflectPage"));
const SummaryPage = lazyWithRetry(() => import("@pages/lessons/SummaryPage"));
const LessonDetailPage = lazyWithRetry(() => import("@pages/lessons/LessonDetailPage"));

// Orientation pages
const FirstOrientationPage = lazyWithRetry(() => import("@pages/studentPlatform/orientation/FirstOrientationPage"));
const OrientationPage = lazyWithRetry(() => import("@pages/studentPlatform/orientation/OrientationPage"));
const AvatarOrientationPage = lazyWithRetry(() => import("@pages/studentPlatform/orientation/AvatarOrientationPage"));
const ActivitiesOrientationPage = lazyWithRetry(() => import("@pages/studentPlatform/orientation/ActivitiesOrientationPage"));
const SeniorAssignmentsOrientationPage = lazyWithRetry(() => import("@pages/studentPlatform/orientation/SeniorAssignmentsOrientationPage"));
const MaterialOrientationPage = lazyWithRetry(() => import("@pages/studentPlatform/orientation/MaterialOrientationPage"));
const CertificateOrientationPage = lazyWithRetry(() => import("@pages/studentPlatform/orientation/CertificateOrientationPage"));
const SubjectsOrientationPage = lazyWithRetry(() => import("@pages/studentPlatform/orientation/SubjectsOrientationPage"));

// Other pages
const DashboardPage = lazyWithRetry(() => import("@pages/DashboardPage"));
const TermPage = lazyWithRetry(() => import("@pages/TermPage"));
const TermsPage = lazyWithRetry(() => import("@pages/TermsPage"));
const WeekLessonsPage = lazyWithRetry(() => import("@/pages/WeekLessonsPage"));
const AllSubjectsPage = lazyWithRetry(() => import("@/pages/AllSubjectsPage"));
const SubjectPage = lazyWithRetry(() => import("@pages/SubjectPage"));
const AchievementsPage = lazyWithRetry(() => import("@/pages/AchievementsPage"));
const GradeMaterialsPage = lazyWithRetry(() => import("@/pages/GradeMaterialsPage"));
const StudentAccountDetailsPage = lazyWithRetry(() => import("@/pages/StudentAccountDetailsPage"));
const AssignmentsPage = lazyWithRetry(() => import("@pages/assignments/AssignmentsPage"));
const AssignmentDetailPage = lazyWithRetry(() => import("@pages/assignments/AssignmentDetailPage"));

// Error pages (lazy with retry — only loaded on error routes)
const MaintenancePage = lazyWithRetry(() => import("@/pages/errors/MaintenancePage"));
const NotFoundPage = lazyWithRetry(() => import("@/pages/errors/NotFoundPage"));
// ServerErrorPage is no longer lazy-loaded as a route — server errors are
// rendered in-place by ThemedLayout via useServerError(). The /server-error
// route now redirects to the dashboard.

const learningRouter = createBrowserRouter([
  {
    // Root layout wraps ALL routes with StudentDataProvider
    path: "/",
    element: <RootLayout />,
    children: [
      {
        path: getBaseRoute(),
        element: <LearningLayout />,
        children: [
          { index: true, element: <StartupRedirectPage /> },
          {
            path: ":gradeId",
            element: <Lazy><DashboardPage /></Lazy>
          },
          {
            path: ":gradeId/orientation",
            element: <Lazy><FirstOrientationPage /></Lazy>
          },
          {
            path: ":gradeId/orientation/welcome",
            element: <Lazy><OrientationPage /></Lazy>
          },
          {
            path: ":gradeId/orientation/avatar",
            element: <Lazy><AvatarOrientationPage /></Lazy>
          },
          {
            path: ":gradeId/orientation/activities",
            element: <Lazy><ActivitiesOrientationPage /></Lazy>
          },
          {
            path: ":gradeId/orientation/assignment",
            element: <Lazy><SeniorAssignmentsOrientationPage /></Lazy>
          },
          {
            path: ":gradeId/orientation/material",
            element: <Lazy><MaterialOrientationPage /></Lazy>
          },
          {
            path: ":gradeId/orientation/certificate",
            element: <Lazy><CertificateOrientationPage /></Lazy>
          },
          {
            path: ":gradeId/orientation/subjects",
            element: <Lazy><SubjectsOrientationPage /></Lazy>
          },
          {
            path: ":gradeId/terms",
            element: <Lazy><TermsPage /></Lazy>
          },
          {
            path: ":gradeId/terms/:termId",
            element: <Lazy><TermPage /></Lazy>
          },
          {
            path: ":gradeId/terms/:termId/:weekId",
            element: <Lazy><WeekLessonsPage /></Lazy>
          },
          {
            path: ":gradeId/account",
            element: <Lazy><StudentAccountDetailsPage /></Lazy>
          },
          {
            path: ":gradeId/materials",
            element: <Lazy><GradeMaterialsPage /></Lazy>
          },
          {
            path: ":gradeId/achievements",
            element: <Lazy><AchievementsPage /></Lazy>
          },
          // Subjects
          {
            path: ":gradeId/subjects",
            element: <Lazy><AllSubjectsPage /></Lazy>
          },
          {
            path: ":gradeId/subjects/:subjectSlug/:termId",
            element: <Lazy><SubjectPage /></Lazy>
          },
          // Assignments
          {
            // BLD2-650: legacy bare /assignments path → 404
            path: ":gradeId/assignments",
            element: <Navigate to={`${getBaseRoute()}not-found`} replace />
          },
          {
            path: ":gradeId/assignments/term/:termId",
            element: <Lazy><AssignmentsPage /></Lazy>
          },
          {
            path: ":gradeId/assignments/:termId/:subjectSlug/:assignmentId",
            element: <Lazy><AssignmentDetailPage /></Lazy>
          }
        ]
      },
      {
        path: `${getBaseRoute()}:gradeId/terms/:termId/:weekId/:lessonSlug/lesson/:lessonId`,
        element: <LessonsLayout />,
        errorElement: <LessonRouteErrorBoundary />,
        children: [
          {
            index: true,
            element: <Lazy><LessonDetailPage /></Lazy>
          },
          {
            path: "introduction",
            element: <Lazy><IntroductionPage /></Lazy>
          },
          {
            path: "learn",
            element: <Lazy><LearnPage /></Lazy>
          },
          {
            path: "practise",
            element: <Lazy><PracticePage /></Lazy>
          },
          {
            path: "apply",
            element: <Lazy><ApplyPage /></Lazy>
          },
          {
            path: "reflection",
            element: <Lazy><ReflectPage /></Lazy>
          },
          {
            path: "summary",
            element: <Lazy><SummaryPage /></Lazy>
          }
        ]
      },
      {
        path: `${getBaseRoute()}`,
        element: <ErrorPageLayout />,
        children: [
          {
            path: "maintenance",
            element: <Lazy><MaintenancePage /></Lazy>
          },
          {
            path: "not-found",
            element: <Lazy><NotFoundPage /></Lazy>
          },
          {
            // Legacy route — server errors are now rendered in-place by
            // ThemedLayout via useServerError(). Redirect anyone who
            // lands here (e.g. from a bookmark) to the dashboard.
            path: "server-error",
            element: <Navigate to={getBaseRoute()} replace />
          }
        ]
      },
      {
        path: `${getBaseRoute()}`,
        element: <AuthPageLayout />,
        children: [
          {
            path: "no-access",
            element: <NoAccessPage />
          }
        ]
      },
      {
        path: `${getBaseRoute()}clear-storage`,
        element: <ClearStoragePage />
      },
    ]
  },
  {
    path: "*",
    element: <Navigate to={`${getBaseRoute()}not-found`} replace />
  }
]);

export default learningRouter;
